import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IDLE, LOADING } from "@constants";
import { getFromServerTopic } from "@utility/slice";

export const reDateTime =
  /^([0-9]{4}-[0-9]{2}-[0-9]{2})[ T]?([0-9]{2}:[0-9]{2}(?::[0-9]{2})?)?(\.[0-9]{3})?Z?$/;

function resolveDate(value) {
  return value ? value?.replace(reDateTime, "$1T$2") : value;
}

const SLICE = "curtailmentNotifications";

const action = (name) => `${SLICE}/${name}`;

export const getFromServer = (action, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, payloadFn });

export const getAllEmailNotificationsWithContacts = getFromServer(
  "getAllEventEmailNotificationsWithContacts",
);

export const getAllEmailTemplates = getFromServer("getAllEventEmailTemplates");

export const saveEventEmailTemplate = getFromServer(
  "saveEventEmailTemplate",
  async (payload) => payload,
);

export const getAllEmailTypes = getFromServer("getAllEventEmailTypes");

export const initializeViewEmailNotificationPage = createAsyncThunk(
  action("initializeViewEmailNotificationPage"),
  async (arg, { dispatch }) =>
    Promise.all([dispatch(getAllEmailNotificationsWithContacts())]),
);

export const initializeManageEmailNotificationPage = createAsyncThunk(
  action("initializeManageEmailNotificationPage"),
  async (arg, { dispatch }) =>
    Promise.all([
      dispatch(getAllEmailTypes()),
      dispatch(getAllEmailTemplates()),
    ]),
);

export const getAllEventCommunicationsForTable = getFromServer(
  "getAllEventCommunicationsForTable",
);

export const getAllEventEmailNotificationsByCommunicationId = getFromServer(
  "getAllEventEmailNotificationsByCommunicationId",
  async (payload) => ({ communicationId: payload }),
);
export const getAllEventTextNotificationsByCommunicationId = getFromServer(
  "getAllEventTextNotificationsByCommunicationId",
  async (payload) => ({ communicationId: payload }),
);
export const getAllEventVoiceNotificationsByCommunicationId = getFromServer(
  "getAllEventVoiceNotificationsByCommunicationId",
  async (payload) => ({ communicationId: payload }),
);

export const slice = createSlice({
  name: SLICE,
  initialState: {
    emailNotifications: [],
    emailTemplates: [],
    emailTypes: [],
    status: LOADING,
    eventCommunications: [],
  },
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(
        getAllEmailNotificationsWithContacts.fulfilled,
        (state, action) => {
          const records = action.payload.response;
          for (let i = 0; i < records.length; i++) {
            records[i].sentTimestamp = records[i].sentTimestamp?.replace(
              reDateTime,
              "$1T$2",
            );
            records[i].openedTimestamp = records[i].openedTimestamp?.replace(
              reDateTime,
              "$1T$2",
            );
            records[i].ackTimestamp = records[i].ackTimestamp?.replace(
              reDateTime,
              "$1T$2",
            );
          }
          state.emailNotifications = records;
        },
      )
      .addCase(getAllEmailTemplates.fulfilled, (state, action) => {
        state.emailTemplates = action.payload.response;
      })
      .addCase(getAllEmailTypes.fulfilled, (state, action) => {
        state.emailTypes = action.payload.response;
      })
      .addCase(saveEventEmailTemplate.fulfilled, (state, action) => {
        const { method, template } = action.payload.response;
        if (method === "save") {
          // Add the new record
          state.emailTemplates = [
            ...state.emailTemplates,
            { ...template, emailTemplateId: action.payload.response.insertId },
          ];
        } else {
          // Replace the old record with the new one
          state.emailTemplates = [
            ...state.emailTemplates.filter(
              (t) => t.emailTemplateId !== template.emailTemplateId,
            ),
            template,
          ];
        }
      })
      .addCase(initializeViewEmailNotificationPage.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(initializeViewEmailNotificationPage.fulfilled, (state) => {
        state.status = IDLE;
      })
      .addCase(initializeManageEmailNotificationPage.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(initializeManageEmailNotificationPage.fulfilled, (state) => {
        state.status = IDLE;
      })
      .addCase(getAllEventCommunicationsForTable.fulfilled, (state, action) => {
        const records = action.payload.response;
        for (let i = 0; i < records.length; i++) {
          records[i].curtailDateStart = resolveDate(
            records[i].curtailDateStart,
          );
          records[i].curtailDateEnd = resolveDate(records[i].curtailDateEnd);
        }
        state.eventCommunications = records;
        state.status = IDLE;
      })
      .addCase(getAllEventCommunicationsForTable.rejected, (state, action) => {
        console.error(
          "getAllEventCommunicationsForTable.rejected state",
          state,
        );
        console.error(
          "getAllEventCommunicationsForTable.rejected action",
          action,
        );
        state.status = IDLE;
      })
      .addCase(
        getAllEventEmailNotificationsByCommunicationId.fulfilled,
        (state, action) => {
          const records = action.payload.response;
          for (let i = 0; i < records.length; i++) {
            records[i].sentTimestamp = resolveDate(records[i].sentTimestamp);
            records[i].openedTimestamp = resolveDate(
              records[i].openedTimestamp,
            );
            records[i].ackTimestamp = resolveDate(records[i].ackTimestamp);
          }
        },
      )
      .addCase(
        getAllEventTextNotificationsByCommunicationId.fulfilled,
        (state, action) => {
          const records = action.payload.response;
          for (let i = 0; i < records.length; i++) {
            records[i].sentTimestamp = resolveDate(records[i].sentTimestamp);
          }
        },
      )
      .addCase(
        getAllEventVoiceNotificationsByCommunicationId.fulfilled,
        (state, action) => {
          const records = action.payload.response;
          for (let i = 0; i < records.length; i++) {
            records[i].sentTimestamp = resolveDate(records[i].sentTimestamp);
            records[i].statusUpdated = resolveDate(records[i].statusUpdated);
          }
        },
      ),
});

export default slice.reducer;
